import { useState, useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMediaQuery } from "react-responsive";
import { useQueryClient } from "@tanstack/react-query";

import {
  AssetDisposalFormInterface,
  AssetModel,
  FileModel,
} from "@interfaces/index";
import { useCommonStore } from "@stores/index";
import {
  QUERY_WIDTHS,
  ASSET_FILE_EXTENSIONS,
  assetDisposalSchema,
  formatAmountOnBlur,
} from "@utils/index";
import { attachmentsService } from "@services/index";
import { useAssetDispose } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  Drawer,
  DrawerHeader,
  DrawerScrollableContainer,
  XSvg,
  Button,
  Form,
  FormRow,
  FormFile,
  DatePicker,
  FormInput,
  FormText,
  AssetItem,
  FileItem,
  TrashXSvg,
} from "@components/index";

export const AssetDisposal = ({
  asset,
  onClose,
  onAssetDetailsClose,
}: {
  asset: AssetModel;
  onClose: () => void;
  onAssetDetailsClose: () => void;
}) => {
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const queryClient = useQueryClient();

  const [date, setDate] = useState<Date | undefined>(new Date());

  const [files, setFiles] = useState<FileModel[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const { mutate: mutateDisposeAsset, isPending: isPendingDisposeAsset } =
    useAssetDispose(
      () => {
        reset();
        notify("success", "Asset successfully removed from business.");
        onClose();
        onAssetDetailsClose();
        setState({ isLoading: false });
        queryClient.invalidateQueries({
          queryKey: ["assetGetOne", asset.uniqueId],
        });
        queryClient.invalidateQueries({
          queryKey: ["assetGetBusinessAssets", asset.userBusiness?.uniqueId],
        });
      },
      ({ message, fields }) => {
        Object.keys(fields || {}).forEach((key) =>
          setError(key as "date" | "amount" | "comment", {
            message: fields![key],
          }),
        );
        notify("error", message);
      },
    );

  useEffect(() => {
    setState({ isLoading: isPendingDisposeAsset });
  }, [isPendingDisposeAsset, setState]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    setError,
    clearErrors,
    setValue,
  } = useForm<AssetDisposalFormInterface>({
    mode: "onBlur",
    disabled: isPendingDisposeAsset,
    resolver: yupResolver(assetDisposalSchema),
  });

  useEffect(() => {
    const date = new Date();
    setDate(date);
    reset({ date });
  }, [reset]);

  const handleSelectDate = (value?: Date) => {
    setDate(value);
    setValue("date", value || new Date());
    clearErrors("date");
  };

  const handleSelectFiles = async (target: HTMLInputElement) => {
    setIsUploading(true);
    if (!target.files?.length) {
      setIsUploading(false);
      return;
    }

    const newFiles = await attachmentsService.getAttachments(target.files);

    if (typeof newFiles === "string") {
      notify("error", newFiles);
      setIsUploading(false);
      return;
    }

    const newFilesWithSizes: FileModel[] = newFiles.map((file, index) => ({
      ...file,
      size: target.files?.[index]?.size || 0,
    }));
    setFiles([...files, ...newFilesWithSizes]);
    clearErrors("files");
    setIsUploading(false);
  };

  const removeFile = (uuid: string) => {
    const newFiles = files.filter((file) => file.uuid !== uuid);
    setFiles(newFiles);
  };

  const onSubmit: SubmitHandler<AssetDisposalFormInterface> = (data) =>
    mutateDisposeAsset({
      ...data,
      files,
      assetId: asset.uniqueId,
      amount: data.amount?.replaceAll(/[^0-9/.]*/g, ""),
    });

  return (
    <Drawer className="drawer-asset-details">
      <DrawerHeader
        onClose={onClose}
        className="h-[70px]"
        title={
          <div className="flex flex-col items-center md:items-start">
            <AssetItem type={asset.type} size="big" name={asset.name} />
            {isMobile && asset.userBusiness?.name && (
              <div className="text-black-54 md:text-300 md:font-bold">
                {asset.userBusiness?.name}
              </div>
            )}
          </div>
        }
      />
      {!isMobile && asset.userBusiness?.name && (
        <div className="px-4 pt-6 text-300 font-bold capitalize text-black-54 md:px-14 md:pt-1">
          {asset.userBusiness?.name}
        </div>
      )}
      <DrawerScrollableContainer>
        <Form
          className="max-h-[calc(100dvh_-_50px)] overflow-y-auto md:max-h-[calc(100dvh_-_104px)]"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormRow
            top={isMobile ? "large" : "medium"}
            className="text-300 font-bold md:text-400"
          >
            Asset information
          </FormRow>
          <FormRow top="small" className="field-asset-date">
            <DatePicker
              parent=".field-asset-date"
              date={date}
              prefix="assetDisposal"
              label="Disposal Date"
              dataTestid="input_disposalDate"
              onSetDate={handleSelectDate}
              error={formState.errors.date?.message}
            />
            <input {...register<"date">("date")} type="hidden" name="date" />
          </FormRow>
          <FormRow top={"small"}>
            <FormInput
              {...register<"amount">("amount")}
              name="amount"
              label="Amount"
              maxLength={13}
              data-testid="input_disposalAmount"
              error={formState.errors.amount?.message}
              onChange={({ currentTarget }) => {
                setValue("amount", currentTarget.value);
                clearErrors("amount");
              }}
              onBlur={({ currentTarget }) =>
                setValue("amount", formatAmountOnBlur(currentTarget.value))
              }
            />
          </FormRow>
          <FormRow top={"small"}>
            <FormText
              {...register<"comment">("comment")}
              name="comment"
              label="Comment"
              data-testid="input_disposalComment"
              error={formState.errors.comment?.message}
              onChange={() => clearErrors("comment")}
            />
          </FormRow>
          <FormRow
            top={isMobile ? "large" : "medium"}
            className="text-300 font-bold md:text-400"
          >
            Receipt
          </FormRow>
          <FormRow top="small">
            <FormFile
              {...register<"files">("files")}
              name="files"
              label="Upload file"
              dataTestid="input_uploadFiles"
              error={formState.errors.files?.message}
              accept={ASSET_FILE_EXTENSIONS}
              uploading={isUploading}
              onChange={(event) => {
                handleSelectFiles(event.target as HTMLInputElement);
                event.preventDefault();
              }}
            />
          </FormRow>
          {!files.length ? null : (
            <div className="mt-4 flex max-w-[430px] flex-col gap-y-4">
              {files.map((file) => (
                <FileItem key={file.uuid} file={file} onRemove={removeFile} />
              ))}
            </div>
          )}
          <FormRow
            isRow={!isMobile}
            isBetween
            top="small"
            className="mt-10 flex flex-col gap-y-4 border-t border-t-black-8 pb-2 md:flex-row md:justify-between md:pb-4 md:pt-4"
          >
            <Button
              type="submit"
              variant="red"
              leftIcon={<TrashXSvg />}
              title="Remove from Business"
              dataTestid="button_removeAsset"
              height="small"
              disabled={!!asset.disposal}
            />
            <Button
              variant="secondary"
              leftIcon={<XSvg />}
              title="Cancel"
              height="small"
              onClick={onClose}
            />
          </FormRow>
        </Form>
      </DrawerScrollableContainer>
    </Drawer>
  );
};
